import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAnalytics } from '@bonusly/analytics';
import { Button, HStack, PlusCircleOutline, Text, UsersOutline } from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';
import { UserDetailEntity } from '@bonusly/user-entities';

import { HandIcon } from '../assets/hand-icon';
import { UserPopoverEvent } from '../types';
import { ActionType } from './user-popover';

interface UserPopoverActionsProps {
	actionType?: ActionType;
	source?: string;
	user: UserDetailEntity;
}

export const UserPopoverActions: FC<UserPopoverActionsProps> = ({ actionType = ActionType.Recognize, source, user }) => {
	const { t } = useTranslation('components');
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { track } = useAnalytics();

	const handleClickGive = () => {
		if (source) {
			track(UserPopoverEvent.UserPopoverRecognizeButtonClick, {
				source: source,
				userId: user.id,
			});
		}

		dispatch('init-give-recognition', {
			recipient: {
				id: user.id,
				displayName: user.displayName,
				profilePic: user.profilePic,
				username: user.username,
			},
		});
	};

	if (actionType === ActionType.ScheduleOneOnOne) {
		const handleScheduleOneOnOne = () => {
			// Track the schedule 1:1 click if source is provided
			if (source) {
				track(UserPopoverEvent.UserPopoverScheduleOneOnOneClicked, {
					source: source,
					userId: user.id,
				});
			}
			navigate(`/team/${user.id}/meet`);
		};

		return (
			<Button backgroundColor="primary.bg" onClick={handleScheduleOneOnOne}>
				<HStack>
					<PlusCircleOutline color="white" />
					<Text>{t('user_popover.scheduleOneOnOne')}</Text>
				</HStack>
			</Button>
		);
	}

	if (actionType === ActionType.OpenOneOnOne) {
		const handleOpenOneOnOne = () => {
			// Track the open 1:1 click if source is provided
			if (source) {
				track(UserPopoverEvent.UserPopoverOpenOneOnOneClicked, {
					source: source,
					userId: user.id,
				});
			}
			navigate(`/team/${user.id}/meet`);
		};

		return (
			<Button backgroundColor="primary.bg" onClick={handleOpenOneOnOne}>
				<HStack>
					<UsersOutline color="white" />
					<Text>{t('user_popover.openOneOnOne')}</Text>
				</HStack>
			</Button>
		);
	}

	return (
		<Button test-dataid="user-popover-recognition-button" backgroundColor="primary.bg" textTransform="capitalize" onClick={handleClickGive}>
			{t('user_popover.recognize')}&nbsp;
			<HandIcon />
		</Button>
	);
};
